/**
 * Service Requests API service
 * Handles all service-request-related API operations
 */

import { httpClient } from '@/utils/httpClient';
import type {
    APIServiceRequest,
    APIServiceRequestRequest,
    DRFPaginatedResponse,
    ServiceRequestStatus,
    UrgencyLevel,
} from '@/types';

/**
 * API endpoints for service requests
 */
const BASE_ENDPOINT = '/service-requests/';
const HAS_HIGH_URGENCY_ENDPOINT = '/service-requests/has-high-urgency/';

const byId = (id: number): string => `${BASE_ENDPOINT}${id}/`;

/**
 * Query params accepted by the list endpoint
 */
export interface ServiceRequestsListParams {
    page?: number;
    page_size?: number;
    status?: ServiceRequestStatus;
    urgency?: UrgencyLevel;
    preferred_date_from?: string;
    preferred_date_to?: string;
}

/**
 * Service requests service implementation
 */
export const serviceRequestsService = {
    async hasHighUrgency(): Promise<boolean> {
        const response = await httpClient.get<boolean | { has_high_urgency?: boolean; hasUrgent?: boolean }>(
            HAS_HIGH_URGENCY_ENDPOINT
        );

        if (typeof response === 'boolean') return response;
        if (typeof response?.has_high_urgency === 'boolean') return response.has_high_urgency;
        if (typeof response?.hasUrgent === 'boolean') return response.hasUrgent;
        return false;
    },

    /**
     * Get paginated list of service requests
     */
    async list(
        params?: ServiceRequestsListParams
    ): Promise<DRFPaginatedResponse<APIServiceRequest>> {
        const searchParams = new URLSearchParams();

        if (params?.page !== undefined) {
            searchParams.set('page', String(params.page));
        }
        if (params?.page_size !== undefined) {
            searchParams.set('page_size', String(params.page_size));
        }
        if (params?.status) {
            searchParams.set('status', params.status);
        }
        if (params?.urgency) {
            searchParams.set('urgency', params.urgency);
        }
        if (params?.preferred_date_from) {
            searchParams.set('preferred_date_from', params.preferred_date_from);
        }
        if (params?.preferred_date_to) {
            searchParams.set('preferred_date_to', params.preferred_date_to);
        }

        const queryString = searchParams.toString();
        const endpoint = queryString
            ? `${BASE_ENDPOINT}?${queryString}`
            : BASE_ENDPOINT;

        return httpClient.get<DRFPaginatedResponse<APIServiceRequest>>(endpoint);
    },

    /**
     * Get a single service request by ID
     */
    async getById(id: number): Promise<APIServiceRequest> {
        return httpClient.get<APIServiceRequest>(byId(id));
    },

    /**
     * Create a new service request
     */
    async create(payload: APIServiceRequestRequest): Promise<APIServiceRequest> {
        return httpClient.post<APIServiceRequest, APIServiceRequestRequest>(
            BASE_ENDPOINT,
            payload,
            {
                successTitle: 'Solicitud creada',
                successMessage: 'La solicitud de servicio se registró correctamente.',
                errorTitle: 'Error en solicitud',
            }
        );
    },

    /**
     * Full update of a service request
     */
    async update(
        id: number,
        payload: APIServiceRequestRequest
    ): Promise<APIServiceRequest> {
        return httpClient.put<APIServiceRequest, APIServiceRequestRequest>(
            byId(id),
            payload,
            {
                successTitle: 'Solicitud actualizada',
                successMessage: 'Los cambios de la solicitud se guardaron.',
                errorTitle: 'Error en solicitud',
            }
        );
    },

    /**
     * Partial update of a service request
     */
    async patch(
        id: number,
        payload: Partial<APIServiceRequestRequest>
    ): Promise<APIServiceRequest> {
        return httpClient.patch<
            APIServiceRequest,
            Partial<APIServiceRequestRequest>
        >(byId(id), payload, {
            successTitle: 'Solicitud actualizada',
            successMessage: 'Se actualizaron los datos de la solicitud.',
            errorTitle: 'Error en solicitud',
        });
    },

    /**
     * Delete a service request
     */
    async remove(id: number): Promise<void> {
        return httpClient.delete<void>(byId(id), {
            successTitle: 'Solicitud eliminada',
            successMessage: 'La solicitud se eliminó correctamente.',
            errorTitle: 'Error en solicitud',
        });
    },
};

export default serviceRequestsService;
